/**
 * Offline queue for voice-QA samples — modelled on `lib/run-queue.ts`.
 *
 * Clip bytes go to `@capacitor/filesystem` (`Directory.Cache`, purgeable by
 * the OS, which is fine for a sample) and an index of entries lives in one
 * `@capacitor/preferences` key. Flushes run serially on enqueue, app boot,
 * foreground resume and network-online, and every outcome is final:
 *
 *   2xx → delete file + entry      (the endpoint only ever answers 204)
 *   4xx → delete file + entry      (it would be refused tomorrow too)
 *   5xx / network → keep, count the attempt, give up after MAX_ATTEMPTS
 *
 * The queue is capped by count, bytes and age, dropping the oldest first, so
 * a tablet that is offline for a month holds a bounded amount of audio and a
 * late upload never carries stale samples. Everything here swallows its own
 * errors: a sample that can't be written, read or sent is simply lost.
 */

import { App } from '@capacitor/app'
import { Directory, Filesystem } from '@capacitor/filesystem'
import { Network } from '@capacitor/network'
import { Preferences } from '@capacitor/preferences'

import { api } from './api'
import { logWarn } from './log'
import { isNative } from './platform'

const INDEX_KEY = 'lingotalk.voice_qa.queue'
const DIR = 'lingotalk/voice-qa'

export const MAX_ENTRIES = 20
export const MAX_BYTES = 25 * 1024 * 1024
export const MAX_AGE_MS = 48 * 60 * 60 * 1000
export const MAX_ATTEMPTS = 5

export interface SampleMeta {
  /** Client-minted UUIDv4; the server files the clip under it, see lib/voice-qa.ts. */
  sample_id: string
  engine: string
  /** How the audio was obtained — see lib/voice-qa.ts `CaptureEngine`. */
  capture: 'tee' | 'parallel'
  language: string
  target: string
  transcript: string
  matched: boolean
  status: string
  attempt_index: number
  audio: {
    sample_rate?: number
    channels?: number
    duration_ms?: number
    rms?: number
  }
  client: {
    platform: string
    os_major: string
    app_version: string
    device_model?: string
  }
}

export interface QueuedSample {
  id: string
  /** Path inside `Directory.Cache`. */
  path: string
  bytes: number
  mime: string
  ext: string
  meta: SampleMeta
  enqueued_at: string
  attempt_count: number
}

// --- index I/O ----------------------------------------------------------------

async function readIndex(): Promise<QueuedSample[]> {
  try {
    const { value } = await Preferences.get({ key: INDEX_KEY })
    if (!value) return []
    const parsed: unknown = JSON.parse(value)
    return Array.isArray(parsed) ? (parsed as QueuedSample[]) : []
  } catch {
    return []
  }
}

async function writeIndex(entries: QueuedSample[]): Promise<void> {
  await Preferences.set({ key: INDEX_KEY, value: JSON.stringify(entries) })
}

export async function listQueuedSamples(): Promise<QueuedSample[]> {
  return readIndex()
}

// --- file I/O -----------------------------------------------------------------

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const result = reader.result
      if (typeof result !== 'string') {
        reject(new Error('FileReader returned non-string result'))
        return
      }
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'))
    reader.readAsDataURL(blob)
  })
}

function base64ToBlob(base64: string, mime: string): Blob {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}

async function writeFile(path: string, blob: Blob): Promise<void> {
  await Filesystem.writeFile({
    path,
    data: await blobToBase64(blob),
    directory: Directory.Cache,
    recursive: true,
  })
}

async function readFile(path: string, mime: string): Promise<Blob> {
  const { data } = await Filesystem.readFile({ path, directory: Directory.Cache })
  return typeof data === 'string' ? base64ToBlob(data, mime) : data
}

async function deleteFile(path: string): Promise<void> {
  try {
    await Filesystem.deleteFile({ path, directory: Directory.Cache })
  } catch {
    /* already gone */
  }
}

// --- public API ---------------------------------------------------------------

export function randomId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Apply the caps, oldest first. Returns the entries to keep and the ones to
 * delete; the caller owns the file deletions.
 */
export function prune(
  entries: QueuedSample[],
  now: number = Date.now(),
): { keep: QueuedSample[]; drop: QueuedSample[] } {
  const drop: QueuedSample[] = []
  let keep = entries.filter((e) => {
    const age = now - Date.parse(e.enqueued_at)
    const stale = Number.isNaN(age) || age > MAX_AGE_MS || e.attempt_count >= MAX_ATTEMPTS
    if (stale) drop.push(e)
    return !stale
  })
  // Oldest first, so trimming from the front drops the oldest.
  keep.sort((a, b) => Date.parse(a.enqueued_at) - Date.parse(b.enqueued_at))
  while (keep.length > MAX_ENTRIES) drop.push(keep.shift() as QueuedSample)
  let total = keep.reduce((sum, e) => sum + e.bytes, 0)
  while (total > MAX_BYTES && keep.length > 0) {
    const victim = keep.shift() as QueuedSample
    total -= victim.bytes
    drop.push(victim)
  }
  keep = keep.filter((e) => !drop.includes(e))
  return { keep, drop }
}

/**
 * Persist one sample and kick off a flush. Never throws; a sample that cannot
 * be written is lost.
 */
export async function enqueueSample(blob: Blob, mime: string, ext: string, meta: SampleMeta): Promise<void> {
  try {
    const id = meta.sample_id || randomId()
    const path = `${DIR}/${id}.${ext}`
    await writeFile(path, blob)
    const entries = await readIndex()
    entries.push({
      id,
      path,
      bytes: blob.size,
      mime,
      ext,
      meta,
      enqueued_at: new Date().toISOString(),
      attempt_count: 0,
    })
    const { keep, drop } = prune(entries)
    await writeIndex(keep)
    for (const victim of drop) await deleteFile(victim.path)
    void flushSamples()
  } catch (e) {
    logWarn('[voice-qa-queue] enqueue failed:', e)
  }
}

let flushing: Promise<void> | null = null

/**
 * Upload everything queued, one at a time. Concurrent calls share one run.
 */
export function flushSamples(): Promise<void> {
  if (flushing !== null) return flushing
  flushing = doFlush().finally(() => {
    flushing = null
  })
  return flushing
}

async function doFlush(): Promise<void> {
  let entries: QueuedSample[]
  try {
    entries = await readIndex()
  } catch {
    return
  }
  if (entries.length === 0) return

  const { keep, drop } = prune(entries)
  for (const victim of drop) await deleteFile(victim.path)

  const url = isNative ? '/patient/voice-samples' : '/therapist/voice-samples'
  const kept: QueuedSample[] = []
  for (const entry of keep) {
    let blob: Blob
    try {
      blob = await readFile(entry.path, entry.mime)
    } catch {
      // The OS purged the cache file. Nothing to send; drop the entry.
      continue
    }
    const form = new FormData()
    form.append('audio', blob, `sample.${entry.ext}`)
    form.append('meta', JSON.stringify(entry.meta))
    try {
      await api.post(url, form, { headers: { 'Content-Type': 'multipart/form-data' } })
      await deleteFile(entry.path)
    } catch (e) {
      const status = pickStatus(e)
      if (status !== null && status < 500) {
        // Refused for good. Drop it.
        await deleteFile(entry.path)
        continue
      }
      kept.push({ ...entry, attempt_count: entry.attempt_count + 1 })
    }
  }
  try {
    await writeIndex(kept)
  } catch {
    /* next flush re-reads whatever survived */
  }
}

/** Drop everything — the server switched this surface off. */
export async function purgeSamples(): Promise<void> {
  try {
    const entries = await readIndex()
    for (const entry of entries) await deleteFile(entry.path)
    await writeIndex([])
  } catch {
    /* best effort */
  }
}

let installed = false

/**
 * Lifecycle wiring: flush now, on network-online and on foreground resume.
 * Idempotent.
 */
export async function installSampleQueue(): Promise<void> {
  if (installed) return
  installed = true
  void flushSamples()
  try {
    void Network.addListener('networkStatusChange', (status) => {
      if (status.connected) void flushSamples()
    })
  } catch {
    /* plugin unavailable in this shell */
  }
  try {
    void App.addListener('appStateChange', (state) => {
      if (state.isActive) void flushSamples()
    })
  } catch {
    /* plugin unavailable in this shell */
  }
}

function pickStatus(err: unknown): number | null {
  if (err && typeof err === 'object') {
    const status = (err as { response?: { status?: unknown } }).response?.status
    if (typeof status === 'number') return status
  }
  return null
}
